// Registro central del theming por personaje (gradientes + fondos).
//
// Antes, cada ruta/pantalla repetía su `bg-gradient-to-b from-… to-…` inline y
// los fondos de hero/video mock usaban hex literales (`bg-[#0d0a1a]`, …). Esto
// había provocado *drift*: el mismo personaje aparecía con variantes del
// gradiente en distintas páginas (Santa terminaba en verde `green-900` vs
// `emerald-900`, Reyes empezaba en `amber-800` vs `amber-900`, …). Este módulo
// es la única fuente de
// verdad: cada superficie resuelve su tema por personaje en lugar de hardcodear
// el gradiente. Ver DESIGN.md §7 (Theming por personaje) y «A converger» #1/#2.
//
// Los valores son strings de clases Tailwind COMPLETAS (no se construyen de
// forma dinámica) para que el escáner de contenido de Tailwind v4 las genere.

import {
  isRatonPerezCharacterId,
  isReyesCharacterId,
  isReyesVideoCharacterId,
  isToothFairyCharacterId,
} from '@/lib/characters'
import { isGenericCharacterId } from '@/lib/generic-characters'

/**
 * Familias visuales clásicas (con landings, guías SEO y assets de marketing
 * propios). Los mapas exhaustivos de las guías (`components/guide-*`) se
 * tipan con esta clave: el catálogo genérico no tiene superficies públicas.
 */
export type ClassicCharacterThemeKey = 'santa' | 'reyes' | 'tooth-fairy' | 'raton-perez'

/**
 * Familias visuales del catálogo genérico. `generic` es el look neutro por
 * defecto; `easter` y `halloween` visten las landings estacionales, que sobre
 * el azul pizarra de `generic` se verían fuera de tono (y las landings
 * quedarían indistinguibles entre sí). `birthday` es la excepción a la regla
 * estacional: viste la landing de cumpleaños, que es el único caso de uso
 * **plano todo el año**, así que su paleta es de fiesta (fucsia + dorado) y
 * deliberadamente NO evoca ninguna época. Hoy solo las usan las landings:
 * `getCharacterThemeKey` sigue mandando todo `generic-*` a `generic`, así que
 * las pantallas de llamada no cambian de aspecto.
 */
export type CatalogCharacterThemeKey = 'generic' | 'easter' | 'halloween' | 'birthday'

export type CharacterThemeKey = ClassicCharacterThemeKey | CatalogCharacterThemeKey

/**
 * Familias con **landing pública propia**: las cuatro clásicas más las
 * estacionales del catálogo. Es el dominio de los mapas exhaustivos de las guías
 * SEO (`components/guide-*`), que necesitan por familia una landing a la que
 * enlazar, una etiqueta y una imagen. `generic` queda fuera: el hub no es un
 * personaje al que una guía pueda dirigir su CTA.
 */
export type LandingCharacterThemeKey =
  | ClassicCharacterThemeKey
  | 'easter'
  | 'halloween'
  | 'birthday'

/**
 * Acentos de contenido sobre fondo oscuro (introducidos por las guías
 * `/guias`; las landings aún llevan sus acentos inline — candidato a
 * converger). Clases Tailwind completas, ver nota del módulo.
 */
export interface CharacterAccent {
  /** Color del marcador de listas y detalles pequeños. */
  bullet: string
  /** Callout/nota destacada (borde + fondo + texto). */
  note: string
  /** Botón CTA primario (fondo + texto). */
  ctaButton: string
  /** Chip/badge del personaje sobre fondos oscuros (fondo + texto). */
  badge: string
}

/**
 * Tokens de las pantallas de videollamada (ready → calling → activa).
 * Introducidos al generalizar las pantallas Runway (`components/avatar-video/`);
 * las familias ANAM/HeyGen los adoptarán al migrar. Clases Tailwind completas.
 */
export interface CharacterVideoCallTheme {
  /** Gradiente suave (fundido a `bg-background`) de las pantallas previas. */
  preludeGradient: string
  /** Gradiente de fondo de la llamada activa. */
  stageGradient: string
  /** Borde de acento fino (spinner del overlay de conexión). */
  accentBorder: string
  /** Borde suave (marco del PiP, anillo del avatar en "llamando"). */
  softBorder: string
  /** Halos animados alrededor del avatar mientras suena. */
  glow: string
  /** Iconos/chispas de acento. */
  accentText: string
  /** Detalles sólidos (puntos de carga). */
  accentSolid: string
  /** Botón primario de "listo para llamar". */
  ctaButton: string
}

export interface CharacterTheme {
  key: CharacterThemeKey
  /** Gradiente de fondo de las superficies de llamada/experiencia del personaje. */
  callGradient: string
  /** Gradiente de fondo de las landings públicas de marketing (`app/[locale]/…`). */
  landingGradient: string
  /** Fondo sólido (clase Tailwind arbitraria) del hero/video mock del personaje. */
  heroBg: string
  /**
   * Gradiente del bloque de avatar en las cards del selector de personajes.
   * A diferencia del resto de tokens, va sobre superficie CLARA (card blanca),
   * con variante dark suave incluida.
   */
  avatarGradient: string
  /** Acentos de contenido sobre el fondo oscuro del personaje. */
  accent: CharacterAccent
  /** Tokens de las pantallas de videollamada. */
  videoCall: CharacterVideoCallTheme
}

// Santa tenía sus tokens atados a lib/brand.ts (era "el personaje insignia").
// Con marcas por dominio (ADR-035) el shell lo decide la marca activa, así que
// Santa lleva sus literales como cualquier otra familia. Mismos valores.
const SANTA_GRADIENT = 'bg-gradient-to-b from-red-900 via-red-800 to-green-900'
const SANTA_HERO_BG = 'bg-[#1a1a2e]'

// El shell de la app lo pone la marca activa (lib/brands); aquí solo hay familias.
export const CHARACTER_THEMES: Record<CharacterThemeKey, CharacterTheme> = {
  santa: {
    key: 'santa',
    avatarGradient: 'bg-gradient-to-br from-red-100 to-red-200 dark:from-red-900/30 dark:to-red-800/30',
    callGradient: SANTA_GRADIENT,
    landingGradient: SANTA_GRADIENT,
    heroBg: SANTA_HERO_BG,
    // Rojo + esmeralda (Navidad); el CTA reutiliza el token de marca
    // `secondary`, igual que el CTA principal de la home.
    accent: {
      bullet: 'text-red-300',
      note: 'border-emerald-200/40 bg-emerald-400/10 text-emerald-100',
      ctaButton: 'bg-secondary text-secondary-foreground',
      badge: 'bg-red-500/20 text-red-100',
    },
    videoCall: {
      preludeGradient: 'bg-gradient-to-b from-red-950/30 via-red-900/20 to-background',
      stageGradient: SANTA_GRADIENT,
      accentBorder: 'border-red-300',
      softBorder: 'border-red-300/70',
      glow: 'bg-red-300/25',
      accentText: 'text-red-300',
      accentSolid: 'bg-red-400',
      ctaButton: 'bg-secondary text-secondary-foreground hover:bg-secondary/90',
    },
  },
  reyes: {
    key: 'reyes',
    avatarGradient: 'bg-gradient-to-br from-amber-100 to-indigo-200 dark:from-amber-900/30 dark:to-indigo-800/30',
    callGradient: 'bg-gradient-to-b from-amber-800 via-purple-900 to-indigo-950',
    landingGradient: 'bg-gradient-to-b from-slate-950 via-blue-950 to-amber-950',
    heroBg: 'bg-[#101320]',
    // Ámbar/dorado, como los CTA de las fichas de rey.
    accent: {
      bullet: 'text-amber-300',
      note: 'border-amber-200/40 bg-amber-400/10 text-amber-100',
      ctaButton: 'bg-amber-300 text-amber-950',
      badge: 'bg-amber-500/20 text-amber-100',
    },
    videoCall: {
      preludeGradient: 'bg-gradient-to-b from-purple-950/30 via-indigo-950/20 to-background',
      stageGradient: 'bg-gradient-to-b from-amber-800 via-purple-900 to-indigo-950',
      accentBorder: 'border-amber-300',
      softBorder: 'border-amber-300/70',
      glow: 'bg-amber-300/25',
      accentText: 'text-amber-300',
      accentSolid: 'bg-amber-400',
      ctaButton: 'bg-amber-300 text-amber-950 hover:bg-amber-200',
    },
  },
  'tooth-fairy': {
    key: 'tooth-fairy',
    avatarGradient: 'bg-gradient-to-br from-violet-100 to-indigo-200 dark:from-violet-900/30 dark:to-indigo-800/30',
    callGradient: 'bg-gradient-to-b from-indigo-900 via-purple-800 to-blue-900',
    landingGradient: 'bg-gradient-to-b from-violet-950 via-indigo-950 to-purple-950',
    heroBg: 'bg-[#0d0a1a]',
    // Violeta, como el CTA principal de la landing del Hada.
    accent: {
      bullet: 'text-violet-300',
      note: 'border-violet-200/40 bg-violet-400/10 text-violet-100',
      ctaButton: 'bg-violet-400 text-violet-950',
      badge: 'bg-violet-500/20 text-violet-100',
    },
    videoCall: {
      preludeGradient: 'bg-gradient-to-b from-indigo-950/30 via-purple-950/20 to-background',
      stageGradient: 'bg-gradient-to-b from-indigo-900 via-purple-800 to-blue-900',
      accentBorder: 'border-violet-300',
      softBorder: 'border-violet-300/70',
      glow: 'bg-violet-300/25',
      accentText: 'text-violet-300',
      accentSolid: 'bg-violet-400',
      ctaButton: 'bg-violet-400 text-violet-950 hover:bg-violet-300',
    },
  },
  'raton-perez': {
    key: 'raton-perez',
    avatarGradient: 'bg-gradient-to-br from-amber-100 to-stone-200 dark:from-amber-900/30 dark:to-stone-800/30',
    callGradient: 'bg-gradient-to-b from-amber-900 via-amber-800 to-stone-800',
    landingGradient: 'bg-gradient-to-b from-amber-950 via-stone-950 to-amber-950',
    heroBg: 'bg-[#1a1510]',
    // Ámbar cálido sobre tonos tierra, como el CTA de la landing del Ratón.
    accent: {
      bullet: 'text-amber-300',
      note: 'border-amber-200/40 bg-amber-500/10 text-amber-100',
      ctaButton: 'bg-amber-400 text-amber-950',
      badge: 'bg-amber-600/20 text-amber-100',
    },
    // Valores portados de las clases que vivían inline en las pantallas
    // raton-perez-video-* (PR #162), unificando micro-variantes de opacidad.
    videoCall: {
      preludeGradient: 'bg-gradient-to-b from-amber-950/30 via-amber-900/20 to-background',
      stageGradient: 'bg-gradient-to-b from-amber-950 via-amber-900 to-stone-900',
      accentBorder: 'border-amber-300',
      softBorder: 'border-amber-300/70',
      glow: 'bg-amber-300/25',
      accentText: 'text-amber-300',
      accentSolid: 'bg-amber-400',
      ctaButton: 'bg-amber-500 text-amber-950 hover:bg-amber-400',
    },
  },
  // Familia del catálogo genérico (personajes de universos variados,
  // LemonSlice on-the-fly): look neutro sin iconografía navideña — azules
  // pizarra/cielo que no compiten con la imagen del personaje.
  generic: {
    key: 'generic',
    avatarGradient: 'bg-gradient-to-br from-slate-100 to-sky-200 dark:from-slate-800/40 dark:to-sky-900/30',
    callGradient: 'bg-gradient-to-b from-slate-900 via-indigo-950 to-slate-950',
    landingGradient: 'bg-gradient-to-b from-slate-950 via-indigo-950 to-slate-950',
    heroBg: 'bg-[#0e1220]',
    accent: {
      bullet: 'text-sky-300',
      note: 'border-sky-200/40 bg-sky-400/10 text-sky-100',
      ctaButton: 'bg-sky-400 text-sky-950',
      badge: 'bg-sky-500/20 text-sky-100',
    },
    videoCall: {
      preludeGradient: 'bg-gradient-to-b from-slate-950/30 via-indigo-950/20 to-background',
      stageGradient: 'bg-gradient-to-b from-slate-900 via-indigo-950 to-slate-950',
      accentBorder: 'border-sky-300',
      softBorder: 'border-sky-300/70',
      glow: 'bg-sky-300/25',
      accentText: 'text-sky-300',
      accentSolid: 'bg-sky-400',
      ctaButton: 'bg-sky-400 text-sky-950 hover:bg-sky-300',
    },
  },
  // Pascua: primavera al atardecer. Verde salvia y amarillo pastel sobre un
  // fondo bastante más claro que el resto — la única familia que no es noche.
  easter: {
    key: 'easter',
    avatarGradient: 'bg-gradient-to-br from-lime-100 to-teal-200 dark:from-lime-900/30 dark:to-teal-900/30',
    callGradient: 'bg-gradient-to-b from-teal-900 via-emerald-900 to-slate-950',
    landingGradient: 'bg-gradient-to-b from-teal-900 via-emerald-900 to-slate-950',
    heroBg: 'bg-[#0d1f1c]',
    accent: {
      bullet: 'text-lime-300',
      note: 'border-lime-200/40 bg-lime-400/10 text-lime-50',
      ctaButton: 'bg-lime-300 text-emerald-950',
      badge: 'bg-lime-400/20 text-lime-50',
    },
    videoCall: {
      preludeGradient: 'bg-gradient-to-b from-teal-950/30 via-emerald-900/20 to-background',
      stageGradient: 'bg-gradient-to-b from-teal-900 via-emerald-900 to-slate-950',
      accentBorder: 'border-lime-300',
      softBorder: 'border-lime-300/70',
      glow: 'bg-lime-300/25',
      accentText: 'text-lime-300',
      accentSolid: 'bg-lime-400',
      ctaButton: 'bg-lime-300 text-emerald-950 hover:bg-lime-200',
    },
  },
  // Cumpleaños: fiesta y confeti. Fucsia sobre ciruela profunda con el CTA en
  // dorado — dos acentos a propósito, que una fiesta no es monocroma.
  //
  // Es la única familia del catálogo que NO es estacional: el cumpleaños cae
  // todo el año, así que la paleta no puede evocar ninguna época (ni el rojo
  // navideño, ni la calabaza de octubre, ni el pastel de primavera). El dorado
  // sobre rosa la separa además de `halloween`, la otra familia con morados.
  birthday: {
    key: 'birthday',
    avatarGradient:
      'bg-gradient-to-br from-fuchsia-100 to-amber-100 dark:from-fuchsia-900/30 dark:to-amber-900/30',
    callGradient: 'bg-gradient-to-b from-rose-950 via-fuchsia-950 to-indigo-950',
    landingGradient: 'bg-gradient-to-b from-rose-950 via-fuchsia-950 to-indigo-950',
    heroBg: 'bg-[#1d0a1a]',
    accent: {
      bullet: 'text-fuchsia-300',
      note: 'border-fuchsia-200/40 bg-fuchsia-400/10 text-fuchsia-50',
      ctaButton: 'bg-amber-300 text-rose-950',
      badge: 'bg-fuchsia-500/25 text-fuchsia-50',
    },
    videoCall: {
      preludeGradient: 'bg-gradient-to-b from-rose-950/30 via-fuchsia-950/20 to-background',
      stageGradient: 'bg-gradient-to-b from-rose-950 via-fuchsia-950 to-indigo-950',
      accentBorder: 'border-fuchsia-300',
      softBorder: 'border-fuchsia-300/70',
      glow: 'bg-fuchsia-300/25',
      accentText: 'text-fuchsia-300',
      accentSolid: 'bg-fuchsia-400',
      ctaButton: 'bg-amber-300 text-rose-950 hover:bg-amber-200',
    },
  },
  // Halloween: calabaza sobre morado profundo. Simpático, no terrorífico — los
  // personajes de la familia son "monstruos buenos" para niños pequeños.
  halloween: {
    key: 'halloween',
    avatarGradient: 'bg-gradient-to-br from-orange-100 to-purple-200 dark:from-orange-900/30 dark:to-purple-900/30',
    callGradient: 'bg-gradient-to-b from-purple-950 via-violet-950 to-neutral-950',
    landingGradient: 'bg-gradient-to-b from-purple-950 via-violet-950 to-neutral-950',
    heroBg: 'bg-[#170d1f]',
    accent: {
      bullet: 'text-orange-300',
      note: 'border-orange-200/40 bg-orange-400/10 text-orange-50',
      ctaButton: 'bg-orange-400 text-purple-950',
      badge: 'bg-orange-500/20 text-orange-50',
    },
    videoCall: {
      preludeGradient: 'bg-gradient-to-b from-purple-950/30 via-violet-950/20 to-background',
      stageGradient: 'bg-gradient-to-b from-purple-950 via-violet-950 to-neutral-950',
      accentBorder: 'border-orange-300',
      softBorder: 'border-orange-300/70',
      glow: 'bg-orange-300/25',
      accentText: 'text-orange-300',
      accentSolid: 'bg-orange-400',
      ctaButton: 'bg-orange-400 text-purple-950 hover:bg-orange-300',
    },
  },
}

/** Resuelve la clave de tema (familia visual) a la que pertenece un personaje. */
export function getCharacterThemeKey(characterId: string): CharacterThemeKey {
  if (isGenericCharacterId(characterId)) return 'generic'
  if (isToothFairyCharacterId(characterId)) return 'tooth-fairy'
  if (isRatonPerezCharacterId(characterId)) return 'raton-perez'
  if (isReyesCharacterId(characterId) || isReyesVideoCharacterId(characterId)) return 'reyes'
  // Santa (`santa`, `santa-audio`) y, por defecto, cualquier id no reconocido.
  return 'santa'
}

/** Devuelve el tema visual de un personaje a partir de su id. */
export function getCharacterTheme(characterId: string): CharacterTheme {
  return CHARACTER_THEMES[getCharacterThemeKey(characterId)]
}
